import { loadSave, type LoadOptions } from './load';
import { SAVE_KEY, type SaveFile, type SaveSlot, type Settings, type SlotId } from './schema';
import { canContinue, continueLast, createSlot, deleteSlot, loadSlot } from './slots';
import type { StorageAdapter } from './storage-adapter';

export type SaveStoreOptions = {
  adapter: StorageAdapter;
  /** ISO 8601 clock, injected so tests are deterministic. */
  now: () => string;
  prefersReducedMotion: boolean;
} & Pick<LoadOptions, 'onEvent' | 'migrations' | 'targetVersion' | 'validate'>;

/** The document in memory plus the slot operations; every change writes the whole document once. */
export interface SaveStore {
  readonly save: SaveFile;
  readonly canContinue: boolean;
  /** Set when a damaged document was set aside while loading (GDD §6.4). */
  readonly notice: string | null;
  /** Migrations that ran on the document while loading; the result is persisted by the next write. */
  readonly migrated: readonly string[];
  newGame(slotId: string, options?: { overwrite?: boolean }): void;
  load(slotId: string): SaveSlot;
  delete(slotId: string): void;
  /** Writes a whole new document once and keeps it as the current one (used by the autosave). */
  write(next: SaveFile): void;
  /** Changes one setting and writes the whole document once (GDD §6.4, "cambio de ajustes"). */
  updateSetting<K extends keyof Settings>(key: K, value: Settings[K]): void;
  /** The last used slot, to resume its run; changes nothing in storage. */
  continueLast(): { slotId: SlotId; slot: SaveSlot };
}

export function createSaveStore({ adapter, now, ...loadOptions }: SaveStoreOptions): SaveStore {
  const loaded = loadSave(adapter, { now, ...loadOptions });
  let save = loaded.save;

  const commit = (next: SaveFile) => {
    adapter.set(SAVE_KEY, JSON.stringify(next));
    save = next;
  };

  return {
    get save() {
      return save;
    },
    notice: loaded.notice,
    migrated: loaded.migrated,
    get canContinue() {
      return canContinue(save);
    },
    newGame: (slotId, options) => commit(createSlot(save, slotId, now(), options)),
    load(slotId) {
      const loaded = loadSlot(save, slotId);
      commit(loaded.save);
      return loaded.slot;
    },
    delete: (slotId) => commit(deleteSlot(save, slotId)),
    write: commit,
    updateSetting: (key, value) => commit({ ...save, settings: { ...save.settings, [key]: value } }),
    continueLast: () => continueLast(save),
  };
}
